import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { DroneManufacturer } from '../../core/data-models/drone-manufacturer.model';
import { DroneModel } from '../../core/data-models/drone-model.model';
import { DroneManufacturerService } from '../../core/data-services/drone-manufacturer.service';
import { DroneModelService } from '../../core/data-services/drone-model.service';
import { withChanges } from '../../core/utils/model-utils';

@Component({
  selector: 'app-drone-catalog',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './drone-catalog.component.html',
  styleUrls: ['./drone-catalog.component.scss']
})
export class DroneCatalogComponent implements OnInit {
  manufacturers: DroneManufacturer[] = [];
  filteredManufacturers: DroneManufacturer[] = [];
  loading = true;
  errorMessage = '';

  // Search
  searchQuery = '';

  constructor(
    private droneManufacturerService: DroneManufacturerService,
    private droneModelService: DroneModelService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.loadManufacturers();
  }

  async loadManufacturers(): Promise<void> {
    this.loading = true;
    this.errorMessage = '';

    try {
      // Fetch manufacturers and models in parallel
      const [manufacturers, models] = await Promise.all([
        firstValueFrom(this.droneManufacturerService.getManufacturers()),
        firstValueFrom(this.droneModelService.getModels()),
      ]);

      // Add model counts to manufacturers
      const modelCounts = countModelsByManufacturer(models);
      this.manufacturers = manufacturers.map(m => withChanges(m, { modelCount: modelCounts.get(m.id) ?? 0 }));
      this.applyFilter();
    } catch (error: any) {
      console.error('Failed to load manufacturers:', error);
      this.errorMessage = 'Failed to load drone manufacturers. Please try again.';
    } finally {
      this.loading = false;
    }
  }

  applyFilter(): void {
    if (!this.searchQuery.trim()) {
      this.filteredManufacturers = [...this.manufacturers];
    } else {
      const query = this.searchQuery.toLowerCase().trim();
      this.filteredManufacturers = this.manufacturers.filter(m =>
        (m.name ?? '').toLowerCase().includes(query) ||
        (m.description && m.description.toLowerCase().includes(query))
      );
    }
  }

  onSearch(): void {
    this.applyFilter();
  }

  viewManufacturer(manufacturer: DroneManufacturer): void {
    this.router.navigate(['/drone-catalog', manufacturer.id]);
  }

  createManufacturer(): void {
    this.router.navigate(['/drone-catalog/create']);
  }

  formatDate(date: Date | string | null | undefined): string {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }
}

function countModelsByManufacturer(models: DroneModel[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const model of models) {
    if (model.manufacturerId) {
      counts.set(model.manufacturerId, (counts.get(model.manufacturerId) ?? 0) + 1);
    }
  }
  return counts;
}
